import React, { useEffect, useState } from 'react';
import { assignmentApi, usersApi } from '../services/api';
import { AdvisorStage, LeadAssignment, User } from '../types';

interface Props {
  leadId: string;
  currentUser: User;
  onClose: () => void;
}

const STAGES: { key: AdvisorStage; label: string }[] = [
  { key: 'presales', label: 'Pre-Sales' },
  { key: 'sitevisit', label: 'Site Visit' },
  { key: 'closure', label: 'Closure' },
];

const AdvisorPanel: React.FC<Props> = ({ leadId, currentUser, onClose }) => {
  const [assignments, setAssignments] = useState<LeadAssignment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [saving, setSaving] = useState<AdvisorStage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, u] = await Promise.all([
          assignmentApi.getForLead(leadId),
          usersApi.list(true),
        ]);
        setAssignments(a.assignments);
        setUsers(u.data);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load');
      }
    })();
  }, [leadId]);

  const byStage = (stage: AdvisorStage) => assignments.find(a => a.stage === stage);

  const canEdit = (stage: AdvisorStage): boolean => {
    if (currentUser.role === 'admin') return true;
    if (stage === 'sitevisit') return byStage('presales')?.user_id === currentUser.id;
    if (stage === 'closure') return byStage('sitevisit')?.user_id === currentUser.id;
    return false;
  };

  const handleChange = async (stage: AdvisorStage, userId: string) => {
    setSaving(stage); setError(null);
    try {
      const next = userId || null;
      await assignmentApi.setStage(leadId, stage, next);
      const fresh = await assignmentApi.getForLead(leadId);
      setAssignments(fresh.assignments);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/30 flex items-center justify-center z-50">
      <div className="bg-warm-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display text-2xl text-charcoal">Advisors</h2>
          <button onClick={onClose} className="text-graphite hover:text-charcoal">✕</button>
        </div>

        {error && <div className="mb-3 p-2 bg-brick/10 text-brick text-sm rounded">{error}</div>}

        <div className="space-y-4">
          {STAGES.map(({ key, label }) => {
            const current = byStage(key);
            const editable = canEdit(key);
            return (
              <div key={key}>
                <label className="block text-xs text-graphite mb-1">{label}</label>
                <select
                  disabled={!editable || saving === key}
                  value={current?.user_id || ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className="w-full px-3 py-2 border border-sketch rounded-lg text-sm disabled:bg-cream disabled:text-graphite"
                >
                  <option value="">— Unassigned —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.email}</option>
                  ))}
                </select>
                {!editable && (
                  <p className="text-xs text-graphite/70 mt-1">
                    {current ? `Only ${currentUser.role === 'admin' ? '' : 'the previous-stage advisor or '}admins can change this.` : 'Read-only.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-forest text-warm-white rounded-lg">Done</button>
        </div>
      </div>
    </div>
  );
};

export default AdvisorPanel;
